import React, { useMemo, useState } from 'react'

import { Modal } from '../../../../../components/Modal/Modal'

import { type User } from '../../../../../models/User'
import { type ManualParticipant, ManualParticipantAddedReason } from '../../../../../models/ManualParticipant'

import { useUpsertManualParticipantMutation } from '../../../../../store/media/media'
import { useAppDispatch } from '../../../../../store/hooks'
import { showToast } from '../../../../../store/meetingSummary/display'

import { type UsersConsent } from '../hooks/users'
import { ManualParticipantReasonFields } from './ManualParticipantReasonFields'
import { getManualParticipantConsentWarningText, isManualParticipantReasonValid, shouldShowManualParticipantConsentWarning } from './utils'

export interface EditManualParticipantModalProps {
  show: boolean
  accountId: string
  roomId: string
  sessionId: string
  user: User | null
  manualParticipant: ManualParticipant | null
  usersConsent: UsersConsent | null
  consentTrackingEnabled: boolean
  onCancel: () => void
  onSuccess: () => Promise<void> | void
}

export const EditManualParticipantModal: React.FC<EditManualParticipantModalProps> = ({
  show,
  accountId,
  roomId,
  sessionId,
  user,
  manualParticipant,
  usersConsent,
  consentTrackingEnabled,
  onCancel,
  onSuccess,
}) => {
  const [reason, setReason] = useState<ManualParticipantAddedReason | null>(manualParticipant?.addedReason ?? null)
  const [otherExplanation, setOtherExplanation] = useState(manualParticipant?.addedReasonExplanation ?? '')

  const dispatch = useAppDispatch()
  const [triggerUpsertManualParticipant, { isLoading: saving }] = useUpsertManualParticipantMutation()

  const showConsentWarning = useMemo(() => {
    return shouldShowManualParticipantConsentWarning(user, usersConsent, reason, consentTrackingEnabled)
  }, [consentTrackingEnabled, reason, user, usersConsent])

  const isFormValid = useMemo(() => {
    return isManualParticipantReasonValid(reason, otherExplanation)
  }, [otherExplanation, reason])

  const handleReasonSelected = (selectedReason: ManualParticipantAddedReason) => {
    setReason(selectedReason)
    if (selectedReason !== ManualParticipantAddedReason.OTHER) {
      setOtherExplanation('')
    }
  }

  const handleSave = async () => {
    if (!user || !manualParticipant || !isFormValid) {
      return
    }

    await triggerUpsertManualParticipant({
      accountId,
      roomId,
      sessionId,
      body: {
        userId: user.id,
        addedReason: reason as ManualParticipantAddedReason,
        addedReasonExplanation: reason === ManualParticipantAddedReason.OTHER ? otherExplanation.trim() : undefined,
      },
    }).unwrap()

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Reason saved' }))
    await onSuccess()
  }

  if (!user || !manualParticipant) {
    return null
  }

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered={true}
      header={<h4>Edit reason</h4>}
      headerBodySeparator={true}
      body={
        <div className='d-flex flex-column gap-4'>
          <p className='paragraph-medium mb-0'>Select or briefly describe why this participant was added.</p>
          <ManualParticipantReasonFields
            reason={reason}
            otherExplanation={otherExplanation}
            onReasonSelected={handleReasonSelected}
            onOtherExplanationChange={setOtherExplanation}
            showConsentWarning={showConsentWarning}
            consentWarningText={getManualParticipantConsentWarningText(user)}
            otherOptionLabel='Other (requires explanation)'
            otherExplanationPlaceholder='Explain the reason'
          />
        </div>
      }
      showFooterActions={true}
      onFooterPrimaryAction={handleSave}
      footerPrimaryActionTitle='Save'
      footerPrimaryActionLoading={saving}
      footerPrimaryActionDisabled={!isFormValid}
      onFooterSecondaryAction={onCancel}
      footerClassName='px-4 pb-4 pt-0'
    />
  )
}
